import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListAllpayUsersQueryKey,
  useCreateAllpayUser,
  useUpdateAllpayUserCreditLimit,
} from '@/api/trafikportalen';
import { FormElement } from '@/components/FormElement';
import { OrganizationCreditInfo } from '@/components/OrganizationCreditInfo';
import { BulkTravellerNamedList } from '@/components/Travellers/BulkTravellerNamedList';
import { BulkTravellerProgress } from '@/components/Travellers/BulkTravellerProgress';
import Button from '@/components/core/Button';
import CheckBox from '@/components/core/Checkbox';
import Input from '@/components/core/Input';
import {
  BulkAllpaySkipReason,
  BulkAllpayTraveller,
  getBulkAllpayCreditConsumption,
  getBulkAllpayPlan,
  isAdditionalBulkAllpayCreate,
} from '@/helpers/bulkAllpayAssign';
import { getTravellerDisplayName } from '@/helpers/bulkTravellerOperation';
import { trimData } from '@/helpers/trimData';
import { useBulkTravellerOperation } from '@/hooks/useBulkTravellerOperation';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { IconCheck } from '@/icons';

interface BulkAllpayAssignFormProps {
  travellers: BulkAllpayTraveller[];
  filteredDepartmentIds: string[];
  onOpenChange: (open: boolean) => void;
  onProcessingChange: (processing: boolean) => void;
  onComplete: (failedTravellerIds: string[]) => void;
}

export const BulkAllpayAssignForm = ({
  travellers,
  filteredDepartmentIds,
  onOpenChange,
  onProcessingChange,
  onComplete,
}: BulkAllpayAssignFormProps) => {
  const { formatMessage, locale } = useIntl();
  const queryClient = useQueryClient();
  const createAllpayUser = useCreateAllpayUser();
  const updateAllpayUser = useUpdateAllpayUserCreditLimit();
  const {
    remainingCredit,
    hasNoCreditLimit,
    isLoading: creditInfoLoading,
  } = useOrgCreditInfo();
  const {
    isProcessing,
    isComplete,
    processedCount,
    progressTotal,
    currentName,
    results,
    remainingTimeLabel,
    showRemainingTime,
    run,
  } = useBulkTravellerOperation({
    onProcessingChange,
    onComplete,
  });

  const [creditString, setCreditString] = useState('');
  const [updateExisting, setUpdateExisting] = useState(false);

  const anonymousName = formatMessage({ defaultMessage: 'Anonym resenär' });

  const skipReasonMessage = useCallback(
    (reason: BulkAllpaySkipReason) => {
      switch (reason) {
        case 'inactive':
          return formatMessage({
            defaultMessage: 'Resenären är inaktiv',
          });
        case 'already-assigned':
          return formatMessage({
            defaultMessage: 'Har redan tjänsteresa i app',
          });
        case 'no-access':
          return formatMessage({
            defaultMessage: 'Du saknar skrivrättighet för avdelningen',
          });
        case 'no-department':
          return formatMessage({
            defaultMessage: 'Ingen giltig avdelning att tilldela',
          });
        case 'no-phone':
          return formatMessage({
            defaultMessage: 'Saknar giltigt mobilnummer',
          });
        case 'ambiguous-department':
          return formatMessage({
            defaultMessage:
              'Kan tilldelas flera avdelningar i det aktuella filtret',
          });
      }
    },
    [formatMessage],
  );

  const plans = useMemo(
    () =>
      travellers.map((traveller) => ({
        traveller,
        plan: getBulkAllpayPlan(
          traveller,
          updateExisting,
          filteredDepartmentIds,
        ),
      })),
    [filteredDepartmentIds, travellers, updateExisting],
  );

  const summary = useMemo(() => {
    return plans.reduce(
      (acc, { traveller, plan }) => {
        if (plan.action === 'create') {
          if (isAdditionalBulkAllpayCreate(traveller, plan)) {
            acc.createAdditional += 1;
          } else {
            acc.create += 1;
          }
        } else if (plan.action === 'update') {
          acc.update += 1;
        } else {
          acc.skip += 1;
        }
        return acc;
      },
      { create: 0, createAdditional: 0, update: 0, skip: 0 },
    );
  }, [plans]);

  const actionablePlans = useMemo(
    () => plans.filter(({ plan }) => plan.action !== 'skip'),
    [plans],
  );

  const additionalCreateTravellers = useMemo(
    () =>
      plans.filter(({ traveller, plan }) =>
        isAdditionalBulkAllpayCreate(traveller, plan),
      ),
    [plans],
  );

  const skippedTravellers = useMemo(
    () => plans.filter(({ plan }) => plan.action === 'skip'),
    [plans],
  );

  const hasAmbiguousDepartment = skippedTravellers.some(
    ({ plan }) =>
      plan.action === 'skip' && plan.reason === 'ambiguous-department',
  );

  const credit = Number(creditString);
  const creditConsumption = useMemo(() => {
    if (
      !creditString ||
      Number.isNaN(credit) ||
      !Number.isInteger(credit) ||
      credit < 0
    ) {
      return null;
    }

    return getBulkAllpayCreditConsumption(
      actionablePlans.map(({ plan }) => plan),
      credit,
    );
  }, [actionablePlans, credit, creditString]);

  const creditError = useMemo(() => {
    if (!creditString) {
      return '';
    }
    if (Number.isNaN(credit) || !Number.isInteger(credit)) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    if (credit < 0) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte vara negativ',
      });
    }
    if (credit > 20000) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte överstiga 20 000 SEK',
      });
    }
    if (
      !creditInfoLoading &&
      !hasNoCreditLimit &&
      creditConsumption !== null &&
      creditConsumption > remainingCredit
    ) {
      return formatMessage(
        {
          defaultMessage:
            'Den sammanlagda kreditförändringen ({amount} SEK) överstiger organisationens kvarvarande kredit ({remaining} SEK)',
        },
        {
          amount: creditConsumption.toLocaleString(locale),
          remaining: remainingCredit.toLocaleString(locale),
        },
      );
    }
    return '';
  }, [
    credit,
    creditConsumption,
    creditInfoLoading,
    creditString,
    formatMessage,
    hasNoCreditLimit,
    locale,
    remainingCredit,
  ]);

  const creditUsage = useMemo(() => {
    if (
      creditConsumption !== null &&
      (summary.create > 0 ||
        summary.createAdditional > 0 ||
        (updateExisting && summary.update > 0))
    ) {
      return creditConsumption;
    }
    return undefined;
  }, [
    creditConsumption,
    summary.create,
    summary.createAdditional,
    summary.update,
    updateExisting,
  ]);

  const canConfirm =
    creditString.length > 0 &&
    !creditError &&
    actionablePlans.length > 0 &&
    !isProcessing &&
    !creditInfoLoading;

  const handleCreditChange = useCallback((value: string) => {
    let nextValue = value;
    if (nextValue !== '0') {
      nextValue = nextValue.replace(/^0+/, '');
    }
    setCreditString(nextValue);
  }, []);

  const handleConfirm = useCallback(() => {
    void run({
      items: actionablePlans,
      canStart: canConfirm,
      getItem: ({ traveller }) => ({
        id: traveller.id,
        name: getTravellerDisplayName(traveller, anonymousName),
      }),
      execute: async ({ traveller, plan }) => {
        if (plan.action === 'create') {
          await createAllpayUser.mutateAsync({
            data: trimData({
              department_id: plan.membership.department_id,
              collection_member_id: plan.membership.collection_member_id,
              phone_number: traveller.phone ?? '',
              credit,
            }),
          });
          return;
        }

        if (plan.action === 'update') {
          for (const user of plan.users) {
            await updateAllpayUser.mutateAsync({
              id: user.id,
              data: { credit },
            });
          }
        }
      },
      afterAll: async () => {
        await queryClient.invalidateQueries({
          queryKey: getListAllpayUsersQueryKey(),
        });
      },
    });
  }, [
    actionablePlans,
    anonymousName,
    canConfirm,
    createAllpayUser,
    credit,
    queryClient,
    run,
    updateAllpayUser,
  ]);

  const handleClose = useCallback(() => {
    if (isProcessing) {
      return;
    }
    onOpenChange(false);
  }, [isProcessing, onOpenChange]);

  return (
    <div className="flex flex-col gap-5">
      {!isProcessing && !isComplete && (
        <>
          <OrganizationCreditInfo includeTitle={false} />
          <FormElement
            id="bulk-allpay-credit"
            required
            label={<FormattedMessage defaultMessage="Kreditgräns" />}
            input={
              <Input
                id="bulk-allpay-credit"
                type="text"
                inputMode="numeric"
                value={creditString}
                onChange={(event) => handleCreditChange(event.target.value)}
                endAdornment={<FormattedMessage defaultMessage="SEK" />}
                error={creditError}
                aria-required="true"
                aria-invalid={!!creditError}
                helperTextReserveSpace
              />
            }
          />
          <CheckBox
            id="bulk-allpay-update-existing"
            checked={updateExisting}
            onCheckedChange={(checked) => setUpdateExisting(checked === true)}
            label={formatMessage({
              defaultMessage:
                'Uppdatera kreditgräns för resenärer som redan har tjänsteresa i app',
            })}
            description={formatMessage({
              defaultMessage:
                'Om detta inte är valt ändras inte befintlig tjänsteresa i app. Resenärer kan fortfarande tilldelas tjänsteresa på en avdelning där de saknar den.',
            })}
          />
          <div className="flex flex-col gap-1 text-text-800">
            {creditUsage !== undefined && (
              <p>
                {creditUsage < 0 ? (
                  <FormattedMessage
                    defaultMessage="{amount} SEK av organisationens kredit frigörs"
                    values={{
                      amount: Math.abs(creditUsage).toLocaleString(locale),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="{amount} SEK av organisationens kvarvarande kredit används"
                    values={{
                      amount: creditUsage.toLocaleString(locale),
                    }}
                  />
                )}
              </p>
            )}
            <p>
              <FormattedMessage
                defaultMessage="{count, plural, one {# resenär vald} other {# resenärer valda}}"
                values={{ count: travellers.length }}
              />
            </p>
            {summary.create > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär tilldelas tjänsteresa i app} other {# resenärer tilldelas tjänsteresa i app}}"
                  values={{ count: summary.create }}
                />
              </p>
            )}
            {summary.createAdditional > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär tilldelas tjänsteresa i app på ytterligare en avdelning} other {# resenärer tilldelas tjänsteresa i app på ytterligare en avdelning}}"
                  values={{ count: summary.createAdditional }}
                />
              </p>
            )}
            {additionalCreateTravellers.length > 0 && (
              <BulkTravellerNamedList
                className="mt-1"
                ariaLabel={formatMessage({
                  defaultMessage:
                    'Resenärer som tilldelas tjänsteresa i app på ytterligare en avdelning',
                })}
                items={additionalCreateTravellers.map(
                  ({ traveller, plan }) => ({
                    id: traveller.id,
                    name: getTravellerDisplayName(traveller, anonymousName),
                    detail: isAdditionalBulkAllpayCreate(traveller, plan)
                      ? formatMessage(
                          {
                            defaultMessage: 'Tilldelas på {department}',
                          },
                          { department: plan.membership.department_name },
                        )
                      : undefined,
                  }),
                )}
              />
            )}
            {updateExisting && summary.update > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär med befintlig tjänsteresa uppdateras} other {# resenärer med befintlig tjänsteresa uppdateras}}"
                  values={{ count: summary.update }}
                />
              </p>
            )}
            {summary.skip > 0 && (
              <p>
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär hoppas över} other {# resenärer hoppas över}}"
                  values={{ count: summary.skip }}
                />
              </p>
            )}
            {skippedTravellers.length > 0 && (
              <BulkTravellerNamedList
                className="mt-1"
                ariaLabel={formatMessage({
                  defaultMessage: 'Resenärer som hoppas över',
                })}
                items={skippedTravellers.map(({ traveller, plan }) => ({
                  id: traveller.id,
                  name: getTravellerDisplayName(traveller, anonymousName),
                  detail:
                    plan.action === 'skip'
                      ? skipReasonMessage(plan.reason)
                      : undefined,
                }))}
              />
            )}
            {hasAmbiguousDepartment && (
              <p className="mt-2 text-text-800">
                <FormattedMessage defaultMessage="Resenärer som kan tilldelas tjänsteresa i app för mer än en avdelning i det aktuella filtret hoppas över. Filtrera på en avdelning för att tilldela dem." />
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleClose}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              startIcon={<IconCheck aria-hidden="true" />}
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              <FormattedMessage defaultMessage="Tilldela" />
            </Button>
          </div>
        </>
      )}

      {(isProcessing || isComplete) && (
        <BulkTravellerProgress
          isProcessing={isProcessing}
          processedCount={processedCount}
          progressTotal={progressTotal}
          currentName={currentName}
          remainingTimeLabel={remainingTimeLabel}
          showRemainingTime={showRemainingTime}
          results={results}
          progressAriaLabel={formatMessage({
            defaultMessage: 'Förlopp för tilldelning av tjänsteresa i app',
          })}
          stayOnPageMessage={
            <FormattedMessage defaultMessage="Lämna inte sidan. Tilldelningen görs en resenär i taget i webbläsaren och avbryts om du navigerar bort." />
          }
          onClose={handleClose}
        />
      )}
    </div>
  );
};
